<template>
  <s-modal
    v-model="visible"
    title="Meu perfil"
    icon="mdiAccountCircleOutline"
    width="min(620px, 95vw)"
    max-height="90vh"
    :persistent="alterado || loading"
    :submit="salvar"
    :actions="actions"
  >
    <section class="perfil-topo">
      <img v-if="avatarAtual" :src="resolveAssetUrl(avatarAtual.icone)" alt="" class="perfil-avatar" />
      <s-icon v-else icon="mdiAccountCircle" size="extra-large" color="text-tertiary" />

      <div class="perfil-identidade">
        <strong>{{ nome || user?.nome }}</strong>
        <span>{{ user?.email }}</span>
        <div class="perfil-selos">
          <s-badge v-if="user?.tipo?.nome" :label="user.tipo.nome" type="info" density="dense" />
          <s-badge v-for="escola in escolas" :key="escola" :label="escola" icon="mdiSchoolOutline" density="dense" />
        </div>
      </div>
    </section>

    <s-card title="Dados pessoais" icon="mdiCardAccountDetailsOutline" divider>
      <s-row>
        <s-col cols="24">
          <s-input v-model="nome" label="Nome" icon="mdiAccountOutline" :max-length="255" required :disabled="loading" />
        </s-col>
        <s-col cols="24">
          <s-input
            :model-value="user?.email"
            label="E-mail"
            icon="mdiEmailOutline"
            disabled
            tooltip="O e-mail é o seu login: só a gestão da rede pode alterá-lo"
          />
        </s-col>
      </s-row>
    </s-card>

    <s-card title="Avatar" icon="mdiEmoticonHappyOutline" divider>
      <s-loading v-if="carregandoAvatares" height="6rem" />
      <div v-else class="avatares" role="radiogroup" aria-label="Avatar">
        <label v-for="avatar in avatares" :key="avatar.id" class="avatar-opcao" v-tooltip="avatar.nome">
          <input v-model="avatarId" type="radio" name="perfil-avatar" :value="Number(avatar.id)" :aria-label="avatar.nome" :disabled="loading" />
          <img :src="resolveAssetUrl(avatar.icone)" alt="" loading="lazy" />
        </label>
      </div>
    </s-card>

    <s-card title="Aparência" icon="mdiPaletteOutline" divider>
      <div class="temas" role="radiogroup" aria-label="Tema">
        <label v-for="tema in TEMAS" :key="tema.valor" class="tema-opcao">
          <input type="radio" name="perfil-tema" :value="tema.valor" :checked="themeStore.current === tema.valor" @change="themeStore.setTheme(tema.valor)" />
          <s-icon :icon="tema.icone" size="medium" :color="tema.cor" />
          <span>{{ tema.nome }}</span>
        </label>
      </div>
    </s-card>
  </s-modal>
</template>

<script setup lang="ts">
import type { UsuarioAvatar } from '~/types/schemas/usuario';
import type { Action } from '@sieg-ad/componentes-vue';
import { AutenticacaoApi } from '~/api/autenticacao/autenticacao';
import { useThemeStore, type ThemeOptions } from '~/stores/theme';
import { useAuthStore } from '~/stores/auth';

const visible = defineModel<boolean>({ required: true });

const authStore = useAuthStore();
const themeStore = useThemeStore();
const { user } = storeToRefs(authStore);

//? O tema muda na hora (é preferência do navegador, não vai para a API); nome e avatar só no Salvar.
const TEMAS: Array<{ valor: ThemeOptions; nome: string; icone: string; cor: string }> = [
  { valor: 'light', nome: 'Claro', icone: 'mdiWhiteBalanceSunny', cor: 'warning' },
  { valor: 'dark', nome: 'Escuro', icone: 'mdiWeatherNight', cor: 'primary' },
];

const nome = ref('');
const avatarId = ref<number | null>(null);
const avatares = ref<UsuarioAvatar[]>([]);
const carregandoAvatares = ref(false);
const loading = ref(false);

const avatarAtual = computed(() =>
  avatares.value.find((avatar) => Number(avatar.id) === avatarId.value) ?? user.value?.avatar ?? null);

const escolas = computed(() => [...new Set((user.value?.educadores ?? [])
  .map((educador) => educador.escola?.nome)
  .filter((escola): escola is string => Boolean(escola)))]);

const alterado = computed(() =>
  nome.value.trim() !== (user.value?.nome ?? '') || avatarId.value !== Number(user.value?.avatar_id ?? 0));

const actions = computed<Action[]>(() => [
  { name: 'Cancelar', type: 'error', icon: 'mdiClose', disabled: loading.value, click: () => { visible.value = false } },
  { name: 'Salvar', type: 'success', icon: 'mdiContentSave', submit: true, loading: loading.value, disabled: !alterado.value },
]);

// METHODS

const salvar = async () => {
  if (loading.value || !alterado.value || !avatarId.value) return;

  loading.value = true;
  try {
    await AutenticacaoApi.perfil({ nome: nome.value.trim(), avatar_id: avatarId.value });
    successToast('Perfil atualizado!');
    visible.value = false;
  }
  catch (error) {
    console.error(error);
    errorToast(apiMessage(error, 'Não foi possível salvar o perfil.'));
  }
  finally {
    loading.value = false;
  }
};

// GETTERS

const loadAvatares = async () => {
  if (avatares.value.length) return;

  carregandoAvatares.value = true;
  try {
    const { data } = await AutenticacaoApi.avatares();
    avatares.value = data ?? [];
  }
  catch (error) {
    console.error(error);
    errorToast(apiMessage(error, 'Não foi possível carregar os avatares.'));
  }
  finally {
    carregandoAvatares.value = false;
  }
};

// LYFECYCLE

// Começa sempre do que está salvo: nada de rascunho de uma abertura anterior.
watch(visible, (aberto) => {
  if (!aberto) return;
  nome.value = user.value?.nome ?? '';
  avatarId.value = user.value?.avatar_id ? Number(user.value.avatar_id) : null;
  loadAvatares();
}, { immediate: true });
</script>

<style scoped>
.perfil-topo {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  padding: 1.25rem;
  border-radius: var(--border-radius);
  background:
    radial-gradient(circle at 100% 0%, rgba(255, 255, 255, 0.08) 0 35%, transparent 36%),
    linear-gradient(135deg, var(--secondary) 30%, color-mix(in srgb, var(--primary) 70%, var(--secondary)));
  color: #fff;
}

.perfil-avatar {
  flex: 0 0 auto;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.85), 0 8px 20px rgba(0, 0, 0, 0.3);
}

.perfil-identidade {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.perfil-identidade strong {
  font-size: 1.2rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.perfil-identidade span {
  font-size: 0.85rem;
  opacity: 0.8;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.perfil-selos {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.35rem;
}

/* Opções de avatar e de tema: rádio nativo escondido, o cartão inteiro é o alvo do clique. */
.avatar-opcao input,
.tema-opcao input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.avatares {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  gap: 0.6rem;
}

.avatar-opcao {
  position: relative;
  display: grid;
  place-items: center;
  aspect-ratio: 1;
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
  transition: border-color var(--ts-fast) ease, transform var(--ts-fast) ease;
}

.avatar-opcao:hover {
  transform: scale(1.06);
}

.avatar-opcao img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-opcao:has(input:checked) {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 25%, transparent);
}

.avatar-opcao:has(input:focus-visible),
.tema-opcao:has(input:focus-visible) {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.temas {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.tema-opcao {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-primary);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: border-color var(--ts-fast) ease, background-color var(--ts-fast) ease;
}

.tema-opcao:hover {
  border-color: var(--primary);
}

.tema-opcao:has(input:checked) {
  border-color: var(--primary);
  background: var(--bg-accent);
  color: var(--text-primary);
  font-weight: 600;
}
</style>
